import { useSession } from "@/app/session";
import { Shell } from "@/app/shell";
import { ThemeSwitcher } from "@/components/theme-switcher";
import { NotFoundPage } from "@/components/ui/not-found-page";
import { ApiKeysListPage } from "@/features/api-keys/list-page";
import { DatasetDetailPage } from "@/features/datasets/detail-page";
import { DatasetsListPage } from "@/features/datasets/list-page";
import { EdaPage } from "@/features/eda/page";
import { InferencePage } from "@/features/inference/page";
import { JobDetailPage } from "@/features/jobs/detail-page";
import { JobsListPage } from "@/features/jobs/list-page";
import { ModelDetailPage } from "@/features/models/detail-page";
import { ModelsListPage } from "@/features/models/list-page";
import { OverviewPage } from "@/features/overview/page";
import { useCurrentProject } from "@/features/projects/current-project";
import { ProjectsListPage } from "@/features/projects/list-page";
import { RecipeCreatePage } from "@/features/recipes/create-page";
import { RecipeDetailPage } from "@/features/recipes/detail-page";
import { RecipesListPage } from "@/features/recipes/list-page";
import { RecipeVersionsPage } from "@/features/recipes/versions-page";
import { TrainPage } from "@/features/training/train-page";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";

interface RouterCtx {
  userId: string;
  projectId: string | null;
  projectName: string | null;
}

// Root layout component: handles loading / unauthenticated states and wraps
// authenticated content in the Shell. This runs inside RouterProvider, which
// is inside SessionProvider and CurrentProjectProvider, so hooks are available.
function RootLayout() {
  const { userId, loading } = useSession();
  const { projectName } = useCurrentProject();

  if (loading) return <p className="text-fg-muted">Loading…</p>;
  if (!userId) {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="space-y-3 text-center">
          <h1 className="text-2xl font-medium tracking-tight">Autolab Console</h1>
          <p className="text-sm text-fg-muted">
            Please open this console from iQ Agent to sign in.
          </p>
          <ThemeSwitcher />
        </div>
      </div>
    );
  }

  return (
    <Shell projectName={projectName ?? undefined}>
      <Outlet />
    </Shell>
  );
}

const rootRoute = createRootRouteWithContext<RouterCtx>()({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    throw redirect({ to: context.projectId ? "/overview" : "/projects" });
  },
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  component: ProjectsListPage,
});
const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overview",
  component: OverviewPage,
});
const datasetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/datasets",
  component: DatasetsListPage,
});
const datasetDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/datasets/$datasetId",
  component: DatasetDetailPage,
});
const edaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/datasets/$datasetId/eda",
  component: EdaPage,
});
const recipesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recipes",
  component: RecipesListPage,
});
const recipeCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recipes/new",
  component: RecipeCreatePage,
});
const recipeVersionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recipes/$recipeId/versions",
  component: RecipeVersionsPage,
});
const recipeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recipes/$recipeId",
  component: RecipeDetailPage,
});
const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/models",
  component: ModelsListPage,
});
const modelDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/models/$modelId",
  component: ModelDetailPage,
});
const inferenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inference",
  validateSearch: z.object({ model: z.string().optional() }),
  component: InferencePage,
});
const trainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/train",
  validateSearch: z.object({ dataset: z.string().optional() }),
  component: TrainPage,
});
const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jobs",
  component: JobsListPage,
});
const jobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jobs/$jobId",
  component: JobDetailPage,
});
const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/api-keys",
  component: ApiKeysListPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  projectsRoute,
  overviewRoute,
  datasetsRoute,
  datasetDetailRoute,
  edaRoute,
  recipesRoute,
  recipeCreateRoute,
  recipeVersionsRoute,
  recipeDetailRoute,
  modelsRoute,
  modelDetailRoute,
  inferenceRoute,
  trainRoute,
  jobsRoute,
  jobDetailRoute,
  apiKeysRoute,
]);

export function buildRouter(ctx: RouterCtx) {
  return createRouter({
    routeTree,
    context: ctx,
    defaultNotFoundComponent: () => {
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      return <NotFoundPage path={path} />;
    },
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
